// All Hosts mode, flat searchable grid of every asset. deterministic
// ordering (by subnet → last octet), simple search box. no circular
// or concentric layout

import React, { useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import type {
  EngagementAsset,
  EngagementAttackRun,
  EngagementEvidence,
  EngagementFinding,
  EngagementPort,
} from '@/lib/engagementsApi'
import { aggregate, RISK_TONES, fallbackCidr } from './topologyAggregates'

interface Props {
  assets:    EngagementAsset[]
  ports:     EngagementPort[]
  attackRuns: EngagementAttackRun[]
  findings:  EngagementFinding[]
  evidence:  EngagementEvidence[]
  onSelect:  (data: Record<string, any> | null) => void
}

export const AllHostsGrid: React.FC<Props> = ({ assets, ports, attackRuns, findings, evidence, onSelect }) => {
  const { byHostIp } = useMemo(
    () => aggregate(assets, [], ports, attackRuns, findings, evidence),
    [assets, ports, attackRuns, findings, evidence]
  )
  const [query, setQuery] = useState('')

  const all = useMemo(() => {
    const list = assets.map(a => byHostIp.get(a.host_ip)!).filter(Boolean)
    return list.sort((a, b) => {
      const sa = a.subnetCidr, sb = b.subnetCidr
      if (sa !== sb) return sa.localeCompare(sb)
      return lastOctet(a.asset.host_ip) - lastOctet(b.asset.host_ip)
    })
  }, [assets, byHostIp])

  const q = query.trim().toLowerCase()
  const filtered = q
    ? all.filter(h =>
        h.asset.host_ip.includes(q) ||
        (h.asset.host_name?.toLowerCase().includes(q) ?? false) ||
        (h.asset.device_type?.toLowerCase().includes(q) ?? false) ||
        (h.subnetCidr.includes(q))
      )
    : all

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-700">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
          {filtered.length} of {all.length} hosts
        </div>
        <label className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search ip / hostname / device / subnet…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="pl-7 pr-2 py-1 text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400 w-72"
          />
        </label>
      </div>
      <div className="flex-1 overflow-auto p-3">
        {filtered.length === 0 ? (
          <div className="text-center text-xs text-slate-400 italic py-12">
            {q ? 'No hosts match the filter.' : 'No hosts captured.'}
          </div>
        ) : (
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10">
            {filtered.map(h => {
              const a = h.asset
              const tone = RISK_TONES[h.topRisk]
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onSelect({
                    type: 'Host', ip: a.host_ip, hostname: a.host_name, mac: a.mac_address,
                    vendor: a.vendor, subnet: a.subnet_cidr ?? fallbackCidr(a.host_ip),
                    deviceType: a.device_type, deviceTypeConfidence: a.device_type_confidence,
                    deviceVendor: a.device_vendor, deviceModel: a.device_model,
                    deviceFirmwareVersion: a.device_firmware_version, asset_id: a.id,
                  })}
                  title={`${a.host_ip}${a.host_name ? ' · ' + a.host_name : ''}`}
                  className={`text-left bg-white dark:bg-slate-800 ${tone.border} border rounded-md p-2 hover:shadow-md transition-shadow`}
                >
                  <div className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">{a.host_ip}</div>
                  {a.host_name && <div className="text-[10px] text-slate-600 dark:text-slate-300 truncate">{a.host_name}</div>}
                  <div className="text-[9px] uppercase tracking-wide text-slate-400 truncate">{a.device_type ?? '—'}</div>
                  <div className="text-[9px] font-mono text-slate-400 truncate">{h.subnetCidr}</div>
                  {(h.attackCount > 0 || h.findings.total > 0) && (
                    <div className="flex gap-1 mt-1">
                      {h.attackCount > 0 && <span className="text-[9px] font-semibold text-rose-600">{h.attackCount}A</span>}
                      {h.findings.total > 0 && <span className="text-[9px] font-semibold text-slate-500">{h.findings.total}F</span>}
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function lastOctet(ip: string): number {
  const parts = ip.split('.')
  const n = parseInt(parts[parts.length - 1], 10)
  return isFinite(n) ? n : 0
}
